import { z } from 'zod'
import {
  addressLookupTableProgramId,
  bpfLoaderProgramId,
  stakeProgramId,
  systemProgramId,
  token2022ProgramId,
  tokenProgramId,
  voteProgramId,
  wrappedSolTokenMintId,
} from '../constants'
import type { Instruction } from '../schemas/instruction'
import type { TransactionDetail } from '../schemas/transactionDetail'
import type { TokenAccountMap, TokenTransfer } from './types'

type ParseInstructionToTransferParameters = {
  readonly index: number
  readonly innerIndex: number | undefined
  readonly instruction: Instruction
  readonly transaction: TransactionDetail
  readonly tokenAccounts: TokenAccountMap
}

export const parseInstructionToTransfer = ({
  index,
  innerIndex,
  instruction,
  transaction,
  tokenAccounts,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  // parse されていない場合は undefined を返す
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.programId) {
    case systemProgramId: {
      return parseSystemInstruction({
        index,
        innerIndex,
        instruction,
        transaction,
        tokenAccounts,
      })
    }
    case stakeProgramId: {
      return parseStakeInstruction({
        index,
        innerIndex,
        instruction,
        transaction,
        tokenAccounts,
      })
    }
    case voteProgramId: {
      // TODO
      throw new Error('Not implemented')
    }
    case addressLookupTableProgramId: {
      console.debug('addressLookupTableProgramId:', instruction.parsed.type)
      return undefined
    }
    case bpfLoaderProgramId: {
      // TODO
      throw new Error('Not implemented')
    }
    case tokenProgramId:
    case token2022ProgramId: {
      switch (instruction.parsed.type) {
        case 'burn': {
          const burnSchema = z.object({
            account: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
            mint: z.string(),
          })
          const { account, amount, mint } = burnSchema.parse(
            instruction.parsed.info,
          )
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: mint,
            from: account,
            to: undefined,
            amount,
          }
        }
        case 'burnChecked': {
          const burnSchema = z.object({
            account: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
            mint: z.string(),
          })
          const {
            account,
            tokenAmount: { amount },
            mint,
          } = burnSchema.parse(instruction.parsed.info)
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: mint,
            from: account,
            to: undefined,
            amount,
          }
        }
        case 'closeAccount': {
          const closeAccountSchema = z.object({
            account: z.string(),
            destination: z.string(),
          })
          const { account, destination } = closeAccountSchema.parse(
            instruction.parsed.info,
          )
          // closeAccount で transfer を処理しても残る sol 残量を取得
          const remain = getSolRemain(transaction, account, tokenAccounts)
          return {
            type: 'native',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            from: account,
            to: destination,
            amount: remain,
          }
        }
        case 'mintTo': {
          const mintSchema = z.object({
            account: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
            mint: z.string(),
          })
          const { account, amount, mint } = mintSchema.parse(
            instruction.parsed.info,
          )
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: mint,
            from: undefined,
            to: account,
            amount,
          }
        }
        case 'mintToChecked': {
          const mintSchema = z.object({
            account: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
            mint: z.string(),
          })
          const { account, tokenAmount, mint } = mintSchema.parse(
            instruction.parsed.info,
          )
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token: mint,
            from: undefined,
            to: account,
            amount: tokenAmount.amount,
          }
        }
        case 'setAuthority': {
          const setAuthoritySchema = z.object({
            account: z.string().optional(),
            mint: z.string().optional(),
            authority: z.string().optional(),
            multisigAuthority: z.string().optional(),
            authorityType: z.string(),
            newAuthority: z.string().nullable(),
          })
          const {
            account,
            authority,
            multisigAuthority,
            authorityType,
            newAuthority,
          } = setAuthoritySchema.parse(instruction.parsed.info)
          const from = authority ?? multisigAuthority
          switch (authorityType) {
            case 'accountOwner': {
              if (account === undefined) {
                throw new Error('setAuthority.account is undefined')
              }
              const amount = BigInt(
                (transaction.meta?.postTokenBalances ?? []).find(
                  (balance) =>
                    transaction.transaction.message.accountKeys[
                      balance.accountIndex
                    ].pubkey === account,
                )?.uiTokenAmount.amount ?? '0',
              )

              const token = tokenAccounts[account]?.mint
              if (token === undefined) {
                throw new Error(`Token account not found: ${account}`)
              }
              return {
                type: 'spl',
                instructionIndex: index,
                innerInstructionIndex: innerIndex,
                token,
                from,
                to: newAuthority ?? undefined,
                amount,
              }
            }
            case 'mintTokens':
            case 'freezeAccount': {
              return undefined
            }
            default: {
              console.debug('Unknown authorityType:', authorityType)
              return undefined
            }
          }
        }
        case 'transfer': {
          const transferSchema = z.object({
            source: z.string(),
            destination: z.string(),
            amount: z.string().regex(/^\d+$/).transform(BigInt),
          })
          const { source, destination, amount } = transferSchema.parse(
            instruction.parsed.info,
          )
          const token =
            tokenAccounts[source]?.mint ?? tokenAccounts[destination]?.mint
          if (token === undefined) {
            throw new Error(
              `Token account not found: ${source} / ${destination}`,
            )
          }
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token,
            from: source,
            to: destination,
            amount,
          }
        }
        case 'transferChecked': {
          const transferSchema = z.object({
            source: z.string(),
            destination: z.string(),
            tokenAmount: z.object({
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            }),
          })
          const { source, destination, tokenAmount } = transferSchema.parse(
            instruction.parsed.info,
          )
          const token =
            tokenAccounts[source]?.mint ?? tokenAccounts[destination]?.mint
          if (token === undefined) {
            throw new Error(
              `Token account not found: ${source} / ${destination}`,
            )
          }
          return {
            type: 'spl',
            instructionIndex: index,
            innerInstructionIndex: innerIndex,
            token,
            from: source,
            to: destination,
            amount: tokenAmount.amount,
          }
        }
        case 'initializeAccount': {
          return undefined
        }
        default: {
          console.debug(
            'Unknown token instruction type:',
            instruction.parsed.type,
          )
          return undefined
        }
      }
    }
    default: {
      return undefined
    }
  }
}

const parseSystemInstruction = ({
  index,
  innerIndex,
  instruction,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.parsed.type) {
    case 'createAccount':
    case 'createAccountWithSeed': {
      const createAccountSchema = z.object({
        lamports: z.number().int().transform(BigInt),
        newAccount: z.string(),
        source: z.string(),
      })
      const { lamports, newAccount, source } = createAccountSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: source,
        to: newAccount,
        amount: lamports,
      }
    }
    case 'transfer':
    case 'transferWithSeed': {
      const transferSchema = z.object({
        lamports: z.number().int().transform(BigInt),
        source: z.string(),
        destination: z.string(),
      })
      const { lamports, source, destination } = transferSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: source,
        to: destination,
        amount: lamports,
      }
    }
    case 'withdrawNonceAccount': {
      // nonce から to への lamports 分の transfer だと思われる
      // TODO
      throw new Error('Not implemented')
    }
    case 'allocate':
    case 'assign': {
      return undefined
    }
    default: {
      console.debug('Unknown system instruction type:', instruction.parsed.type)
      return undefined
    }
  }
}

const parseStakeInstruction = ({
  index,
  innerIndex,
  instruction,
}: ParseInstructionToTransferParameters): TokenTransfer | undefined => {
  if (!('parsed' in instruction)) {
    return undefined
  }

  switch (instruction.parsed.type) {
    case 'initialize':
    case 'initializeChecked':
    case 'authorize':
    case 'authorizeWithSeed':
    case 'authorizeChecked':
    case 'authorizeCheckedWithSeed': {
      return undefined
    }
    case 'delegateStake': {
      throw new Error('Not implemented')
    }
    case 'split': {
      const splitSchema = z.object({
        // "lamports": 119889354,
        lamports: z.number().int().transform(BigInt),
        // "newSplitAccount": "89JRFCBDT3kDZFDi5Vh6hskfhxA44Cmgmgjj1TokPBVS",
        newSplitAccount: z.string(),
        // "stakeAccount": "HD63esu4hWwuVwiHBLKAtukLCJLogrsd97mGYWUG1etn",
        stakeAccount: z.string(),
        // "stakeAuthority": "2oFvntttTFoEejX5uRoP5NLovNjhe7fiCZhs3Td1tpYa"
        stakeAuthority: z.string(),
      })
      const { lamports, newSplitAccount, stakeAccount } = splitSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: stakeAccount,
        to: newSplitAccount,
        amount: lamports,
      }
    }
    case 'withdraw': {
      const withdrawSchema = z.object({
        // "destination": "BgKUXdS29YcHCFrPm5M8oLHiTzZaMDjsebggjoaQ6KFL",
        destination: z.string(),
        // "lamports": 2282880,
        lamports: z.number().int().transform(BigInt),
        // "stakeAccount": "D4n39Kk3FCXWFUhjosBW5tmHMuBX2WoD8asWwjk85puv",
        stakeAccount: z.string(),
      })
      const { destination, lamports, stakeAccount } = withdrawSchema.parse(
        instruction.parsed.info,
      )
      return {
        type: 'native',
        instructionIndex: index,
        innerInstructionIndex: innerIndex,
        from: stakeAccount,
        to: destination,
        amount: lamports,
      }
    }
    case 'deactivate': {
      return undefined
    }
    case 'setLockup': {
      throw new Error('Not implemented')
    }
    case 'merge': {
      // merge は merge amount が不明なので処理できない
      return undefined
    }
    case 'setLockupChecked': {
      throw new Error('Not implemented')
    }
    case 'getMinimumDelegation': {
      return undefined
    }
    case 'deactivateDelinquent': {
      return undefined
    }
    case 'redelegate': {
      return undefined
    }
    case 'moveStake': {
      throw new Error('Not implemented')
    }
    case 'moveLamports': {
      throw new Error('Not implemented')
    }
    default: {
      console.debug('Unknown system instruction type:', instruction.parsed.type)
      return undefined
    }
  }
}

const getSolRemain = (
  transaction: TransactionDetail,
  account: string,
  tokenAccounts: TokenAccountMap,
): bigint => {
  const accountIndex = transaction.transaction.message.accountKeys.findIndex(
    (key) => key.pubkey === account,
  )
  if (accountIndex === -1) {
    throw new Error(`${account}'s accountKeys not found`)
  }
  const preBalance = transaction.meta?.preBalances[accountIndex]
  const postBalance = transaction.meta?.postBalances[accountIndex]
  if (preBalance === undefined || postBalance === undefined) {
    throw new Error('preBalance or postBalance is undefined')
  }
  let mut_remain = BigInt(preBalance) - BigInt(postBalance)
  for (const instruction of [
    ...transaction.transaction.message.instructions,
    ...(transaction.meta?.innerInstructions?.flatMap(
      (innerInstruction) => innerInstruction.instructions,
    ) ?? []),
  ]) {
    if (!('parsed' in instruction)) {
      continue
    }
    switch (instruction.programId) {
      case systemProgramId: {
        const transfer = parseSystemInstruction({
          index: 0,
          innerIndex: undefined,
          instruction,
          transaction,
          tokenAccounts,
        })
        if (transfer === undefined) {
          continue
        }
        if (transfer.from === account) {
          mut_remain -= transfer.amount
        }
        if (transfer.to === account) {
          mut_remain += transfer.amount
        }
        break
      }
      case tokenProgramId:
      case token2022ProgramId: {
        switch (instruction.parsed.type) {
          case 'transfer': {
            const transferSchema = z.object({
              source: z.string(),
              destination: z.string(),
              amount: z.string().regex(/^\d+$/).transform(BigInt),
            })
            const { source, destination, amount } = transferSchema.parse(
              instruction.parsed.info,
            )
            const tokenAccountInfo = tokenAccounts[source]
            if (
              tokenAccountInfo === undefined ||
              tokenAccountInfo.mint !== wrappedSolTokenMintId
            ) {
              continue
            }
            if (source === account) {
              mut_remain -= amount
            }
            if (destination === account) {
              mut_remain += amount
            }
            break
          }
          case 'transferChecked': {
            const transferSchema = z.object({
              source: z.string(),
              destination: z.string(),
              tokenAmount: z.object({
                amount: z.string().regex(/^\d+$/).transform(BigInt),
              }),
            })
            const {
              source,
              destination,
              tokenAmount: { amount },
            } = transferSchema.parse(instruction.parsed.info)
            const tokenAccountInfo = tokenAccounts[source]
            if (
              tokenAccountInfo === undefined ||
              tokenAccountInfo.mint !== wrappedSolTokenMintId
            ) {
              continue
            }
            if (source === account) {
              mut_remain -= amount
            }
            if (destination === account) {
              mut_remain += amount
            }
            break
          }
          default: {
            continue
          }
        }
        break
      }
      default: {
        continue
      }
    }
  }
  return mut_remain
}
